import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";

const SITE_NAME = "León Gallery";

function stripHtml(value = "") {
  return String(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function formatDate(value) {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function LotSEO({ data }) {
  const { slug, id } = useParams();
  const auction = data?.[0] || {};

  const seo = useMemo(() => {
    const auctionName =
      stripHtml(auction.auction_name || auction.title) || "Auction";
    const date = formatDate(auction.date_start);

    const title = `${auctionName} | ${SITE_NAME}`;

    const description = [
      auctionName,
      date ? `Auction date: ${date}.` : "",
      "View lots, artworks, artists, and auction information at León Gallery.",
    ]
      .filter(Boolean)
      .join(" ");

    const canonical =
      slug && id
        ? `https://leon-gallery.com/auction/${String(slug).trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}/${id}`
        : typeof window !== "undefined"
          ? `${window.location.origin}${window.location.pathname}`
          : "";

    const image =
      auction.image ||
      auction.image_url ||
      auction.banner ||
      auction.banner_url ||
      auction.thumbnail ||
      "https://leon-gallery.com/static/media/dddd.f96bbcbee8c8c2f5e3ee.png";

    return {
      title,
      description,
      canonical,
      image,
      structuredData: {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: auctionName,
        description,
        ...(canonical ? { url: canonical } : {}),
        isPartOf: {
          "@type": "WebSite",
          name: SITE_NAME,
        },
      },
    };
  }, [auction, slug, id]);

  return (
    <Helmet>
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />

      {seo.canonical && (
        <link rel="canonical" href={seo.canonical} />
      )}

      <meta property="og:type" content="website" />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:image" content={seo.image} />
      <meta property="og:image:alt" content={seo.title} />

      {seo.canonical && (
        <meta property="og:url" content={seo.canonical} />
      )}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.image} />

      <script type="application/ld+json">
        {JSON.stringify(seo.structuredData)}
      </script>
    </Helmet>
  );
}

export default LotSEO;
